import { isDevMode, NgModule, ErrorHandler, Inject, PLATFORM_ID } from '@angular/core';
import { BrowserModule, Title, Meta } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HeaderInterceptor } from './shared/http-interceptors/header-interceptor';
import { ToastrModule } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { CookieModule } from 'ngx-cookie';
import { provideNgxMask, NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { LoaderComponent } from './shared/components/loaders/loader.component';
import { environment } from 'src/environments/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgbModule, NgbNavModule, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { DownloadPromptComponent } from './shared/components/download-prompt/download-prompt.component';
import { WebinarPopupComponent } from './shared/components/webinar-popup/webinar-popup.component';
import { DisclaimerComponent } from './pages/disclaimer/disclaimer.component';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { NgxGaugeModule } from 'ngx-gauge';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgChartsModule } from 'ng2-charts';
import { Ng2GoogleChartsModule } from 'ng2-google-charts';
import {
   SocialLoginModule,
   SocialAuthServiceConfig,
   FacebookLoginProvider
} from '@abacritt/angularx-social-login';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { watchlistSymbolsReducer } from './stores/watchlist-symbols/watchlist-symbols.reducers';
import { WatchlistSymbolsEffects } from './stores/watchlist-symbols/watchlist-symbols.effects';
import { PermissionsReducer } from './stores/access-control/access-control.reducers';
import { PermissionsEffects } from './stores/access-control/access-control.effects';
import { GlobalErrorHandlerService } from './core/services/global-error-handler.service';

import { portfolioReducer } from './stores/portfolio/portfolio.reducer';
import { PortfolioEffects } from './stores/portfolio/portfolio.effects';
import { sidebarReducer } from './stores/sidebar/sidebar.reducer';
import { layoutReducer } from './stores/layout/layout.reducer';
import { globalReducer } from './stores/global/global.reducer';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { StockOverviewModule } from './modules/stock-overview/stock-overview.module';
import { RibaModalComponent } from './shared/components/riba-modal/riba-modal.component';
import { PreTradingPopupComponent } from './shared/components/pre-trading-popup/pre-trading-popup.component';
import { sidebarMetaReducer } from './stores/sidebar/sidebar.meta-reducer';
import { DisclaimerPopupComponent } from './shared/components/disclaimer-popup/disclaimer-popup.component';
import { InvestInvitationPopupComponent } from './shared/components/invest-invitation-popup/invest-invitation-popup.component';
import { AssetSearchModalComponent } from './shared/components/asset-search-modal/asset-search-modal.component';
import { EarningsCalendarComponent } from './features/earnings-calendar/earnings-calendar.component';

@NgModule({
   declarations: [
      AppComponent,
      LoaderComponent,
      WebinarPopupComponent,
      DisclaimerComponent,
      EarningsCalendarComponent,
      RibaModalComponent,
      PreTradingPopupComponent,
      DisclaimerPopupComponent,
      InvestInvitationPopupComponent,
      AssetSearchModalComponent
   ],
   imports: [
      BrowserModule,
      CommonModule,
      BrowserAnimationsModule,
      FormsModule,
      ReactiveFormsModule,
      AppRoutingModule,
      DragDropModule,
      NgxGaugeModule,
      CarouselModule,
      NgChartsModule,
      NgbDatepickerModule,
      Ng2GoogleChartsModule,
      ToastrModule.forRoot({
         positionClass: 'toast-top-right',
         preventDuplicates: true
      }),
      CookieModule.forRoot(),
      NgxMaskDirective,
      NgxMaskPipe,
      MatProgressSpinnerModule,
      NgbModule,
      NgbNavModule,
      DownloadPromptComponent,
      SocialLoginModule,
      NgSelectModule,
      StoreModule.forRoot({
         watchlistSymbols: watchlistSymbolsReducer,
         permissions: PermissionsReducer,
         portfolio: portfolioReducer,
         sidebar: sidebarReducer,
         layout: layoutReducer,
         global: globalReducer
      }, {
         metaReducers: [sidebarMetaReducer],
         runtimeChecks: {
            strictStateImmutability: true,
            strictActionImmutability: true
         }
      }),
      EffectsModule.forRoot([
         WatchlistSymbolsEffects,
         PermissionsEffects,
         PortfolioEffects,
      ]),
      StockOverviewModule
   ],
   providers: [
      {
         provide: 'SocialAuthServiceConfig',
         useValue: {
            autoLogin: false,
            providers: [
               {
                  id: FacebookLoginProvider.PROVIDER_ID,
                  provider: new FacebookLoginProvider('1375528286183693'),
               },
            ],
            onError: (err: unknown) => {
               console.error(err);
            },
         } as SocialAuthServiceConfig,
         deps: [PLATFORM_ID]
      },
      { provide: HTTP_INTERCEPTORS, useClass: HeaderInterceptor, multi: true },
      Title,
      Meta,
      { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
      provideNgxMask(),
      provideHttpClient(withInterceptorsFromDi()),
   ],
   exports: [
      AppComponent,
      CommonModule,
      BrowserModule,
      BrowserAnimationsModule,
      FormsModule,
      ReactiveFormsModule,
      AppRoutingModule,
      DragDropModule,
      NgxGaugeModule,
      CarouselModule,
      NgChartsModule,
      NgbModule,
      NgbNavModule,
      NgSelectModule,
      NgbDatepickerModule,
      Ng2GoogleChartsModule,
      ToastrModule,
      CookieModule,
      NgxMaskDirective,
      NgxMaskPipe,
      MatProgressSpinnerModule,
      StoreModule,
      EffectsModule,
      StockOverviewModule,
   ]
})
export class AppCommonModule { }
